import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Check, CheckCircle, X } from 'lucide-react';
import { sendEmail } from '../../Services/APIs/dashboard';
import { startChatThread } from '../../Services/APIs/chat';
import { templates } from './templates';
import { getClinicDetails } from '../../Utils/clinicAuth';

const EmailTemplatePopup = ({ onClose, patientEmail, patientData }) => {
    const navigate = useNavigate();
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [subject, setSubject] = useState('Your Smile Preview Results');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const extractChatTokenFromConflict = (payload) => {
        if (!payload) return null;

        if (typeof payload?.chatToken === 'string') {
            return payload.chatToken;
        }

        if (typeof payload?.data?.chatToken === 'string') {
            return payload.data.chatToken;
        }

        if (typeof payload?.existingChatToken === 'string') {
            return payload.existingChatToken;
        }

        if (typeof payload?.message === 'string') {
            const matchedChatToken = payload.message.match(/\bchat_[A-Za-z0-9_-]+\b/);
            return matchedChatToken ? matchedChatToken[0] : null;
        }

        return null;
    };

    // Get clinic data from the clinicDetails cache (populated at login/OTP-verify)
    const getUserData = () => getClinicDetails() || {};

    // Replace placeholders with actual values
    const replacePlaceholders = (text) => {
        const userData = getUserData();
        return text
            .replace(/\{\{patientName\}\}/g, patientData?.name || 'Patient')
            .replace(/\{\{Doctor Name\}\}/g, userData.doctor_name || 'doctor_name')
            .replace(/\{\{Practice Name\}\}/g, userData.practise_name || 'practise_name');
    };

    const handleTemplateChange = (e) => {
        const templateId = e.target.value;
        setSelectedTemplate(templateId);

        const template = templates.find(t => t.id.toString() === templateId);
        if (template) {
            setSubject(replacePlaceholders(template.subject));
            setMessage(replacePlaceholders(template.message));
        } else {
            setSubject('Your Smile Preview Results');
            setMessage('');
        }
    };

    const handleSendEmail = async () => {
        // Reset error state
        setError(null);

        // Helper to validate Mongoose ID
        const isValidMongooseId = (id) => {
            return id && typeof id === 'string' && id.match(/^[0-9a-fA-F]{24}$/);
        };

        // Validate inputs
        if (!patientEmail?.trim()) {
            setError('Patient email is required');
            return;
        }

        if (!subject.trim()) {
            setError('Subject is required');
            return;
        }

        if (!message.trim()) {
            setError('Message is required');
            return;
        }

        const userData = getUserData();
        if (!userData.doctor_name?.trim()) {
            setError('Sender name (Doctor Name) is required. Please ensure your profile is complete.');
            return;
        }

        if (!patientData?._id || !isValidMongooseId(patientData._id)) {
            setError('Invalid patient ID. Please ensure patient data is correctly loaded.');
            return;
        }

        if (!userData?._id?.trim()) {
            setError('Company is required to create a chat thread. Please ensure your account is configured correctly.');
            return;
        }

        // Check if underscore placeholders are still present
        if (message.includes('______')) {
            setError('Please fill in all underscore placeholders (______) with actual values before sending');
            return;
        }

        // Check if Range placeholders are still present
        if (message.includes('{{Range}}')) {
            setError('Please fill in all {{Range}} placeholders with actual pricing information before sending');
            return;
        }

        if (subject.length > 200) {
            setError('Subject must not exceed 200 characters');
            return;
        }

        if (message.length > 10000) {
            setError('Message must not exceed 10000 characters');
            return;
        }

        setSending(true);
        try {
            const userData = getUserData(); // Ensure userData is available here
            const chatPayload = {
                customerEmail: patientEmail.trim(),
                subject: subject.trim(),
                message: message.trim(),
                sentBy: (userData.doctor_name || 'Doctor').trim(),
                patient: (patientData._id), // Use patientData._id for patient field
                company: userData._id.trim(),
                // createdBy: (userData.doctor_name || 'Doctor').trim(),
            };
            await startChatThread(chatPayload);
            setSuccess(true);
            // Close popup after 2 seconds on success
            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (err) {
            const conflictChatToken =
                err?.response?.status === 409
                    ? extractChatTokenFromConflict(err?.response?.data)
                    : null;

            if (conflictChatToken) {
                onClose();
                navigate(`/dashboard/messages/${conflictChatToken}`);
                return;
            }

            console.error('Error sending email:', err);
            setError(err?.response?.data?.message || err.message || 'Failed to send email. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {/* Background Overlay */}
            <div
                className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
                onClick={onClose}
                style={{ pointerEvents: 'auto' }}
            />
            {/* Dialog — capped at 90vh and scrollable, or the form runs off a phone screen */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="email-template-title"
                className="fixed top-[50%] left-[50%] z-50 w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-lg border bg-white p-4 md:p-6 shadow-lg"
                tabIndex="-1"
                style={{ pointerEvents: 'auto' }}
            >
                <h2 id="email-template-title" className="text-lg leading-none font-semibold text-[#2D2D2D] pr-8">
                    Send Email to Patient
                </h2>
                <div className="space-y-4 md:space-y-6 mt-4">
                    {success && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-green-800">Email sent successfully!</p>
                                <p className="text-xs text-green-700">Redirecting...</p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-red-800">{error}</p>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm text-[#2D2D2D] mb-2 font-dashboard">EMAIL TEMPLATES</label>
                        <select
                            disabled={sending}
                            className="w-full px-4 py-2 bg-[#F5F3F0] border border-[#E8E6E3] rounded-lg text-[#2D2D2D] focus:outline-none focus:ring-2 focus:ring-[#38433A] disabled:opacity-50 disabled:cursor-not-allowed"
                            value={selectedTemplate}
                            onChange={handleTemplateChange}
                        >
                            <option value="">Select a template...</option>
                            {templates.map((template) => (
                                <option key={template.id} value={template.id.toString()}>
                                    {template.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-[#2D2D2D] mb-2 font-dashboard">TO:</label>
                        <input
                            type="email"
                            disabled={sending}
                            className="w-full px-4 py-2 bg-[#F5F3F0] border border-[#E8E6E3] rounded-lg text-[#2D2D2D] focus:outline-none focus:ring-2 focus:ring-[#38433A] disabled:opacity-50 disabled:cursor-not-allowed"
                            value={patientEmail}
                            readOnly
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm text-[#2D2D2D] font-dashboard">SUBJECT:</label>
                            <span className="text-xs text-[#6B6B6B]">{subject.length}/200</span>
                        </div>
                        <input
                            type="text"
                            disabled={sending}
                            maxLength="200"
                            className="w-full px-4 py-2 bg-[#F5F3F0] border border-[#E8E6E3] rounded-lg text-[#2D2D2D] focus:outline-none focus:ring-2 focus:ring-[#38433A] disabled:opacity-50 disabled:cursor-not-allowed"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm text-[#2D2D2D] font-dashboard">MESSAGE:</label>
                            <span className="text-xs text-[#6B6B6B]">{message.length}/10000</span>
                        </div>
                        <textarea
                            disabled={sending}
                            maxLength="10000"
                            className="w-full resize-none rounded-xl border border-[#E8E6E3] bg-[#F5F3F0] px-3 py-2 text-sm min-h-32 md:min-h-48 outline-none transition-colors focus:border-[#38433A] disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder=""
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        ></textarea>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        disabled={sending || success}
                        className="px-6 py-2 text-[#6B6B6B] hover:text-[#2D2D2D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        disabled={sending || success}
                        className="px-6 py-2 bg-[#38433A] text-white rounded-lg hover:bg-[#2C352E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        onClick={handleSendEmail}
                    >
                        {sending ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                                Sending...
                            </>
                        ) : success ? (
                            <>
                                <Check className="w-4 h-4" />
                                Sent
                            </>
                        ) : (
                            'Send Email'
                        )}
                    </button>
                </div>
                <button
                    type="button"
                    className="absolute top-4 right-4 text-[#6B6B6B] opacity-70 transition-opacity hover:opacity-100"
                    onClick={onClose}
                >
                    <X className="w-4 h-4" />
                    <span className="sr-only">Close</span>
                </button>
            </div>
        </>
    );
};

export default EmailTemplatePopup;
